/**
 * The HT2026 lecture catalogue on the client (slice B, P1-1/P1-2): the public
 * `GET /api/content/courses/:id` (dates, titles, objectives, topics, pool size) and where the
 * course is today. The day is the Europe/Stockholm calendar day (`shared/time.ts`), the same one
 * the server's Dagens pass uses, so the card and the plan never disagree about "nästa".
 */
import { useQuery } from "@tanstack/react-query";
import type { BookRef } from "@shared/types";
import { CURRENT_COURSE_ID, lectureTimeline, type LectureTimeline } from "@shared/courseCalendar";
import { examModeState, type ExamModeState } from "@shared/examPeriod";
import { useAuth } from "@client/app/auth";
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import type { PublicCourse, PublicLecture } from "@content/courseSchema";
import type { Lang } from "@shared/api";
import { api } from "@client/api/client";
import type { TFunction } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { compressSections } from "./bookRef";

/** The one course this deployment serves (`/practice?lecture=F01` also means HT2026). */
export const COURSE_ID = CURRENT_COURSE_ID;

export const courseQueryKey = (id: string) => ["content", "course", id] as const;

export function useCourse(id: string = COURSE_ID) {
  return useQuery({
    queryKey: courseQueryKey(id),
    queryFn: () =>
      api.get<{ course: PublicCourse }>(`/api/content/courses/${encodeURIComponent(id)}`),
    // P1-12: `release` flips when a lecture opens, so the catalogue is refetched often.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Today's calendar day in the app timezone. */
export function courseToday(now: number = Date.now()): string {
  return dayKey(now, DEFAULT_TIMEZONE);
}

/** The course's lectures in date order, with done/today/next for `today`. */
export function useLectureTimeline(): {
  timeline: LectureTimeline<PublicLecture> | null;
  course: PublicCourse | null;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useCourse();
  const course = query.data?.course ?? null;
  return {
    timeline: course ? lectureTimeline(course.lectures, courseToday()) : null,
    course,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Tentaläge (slice D, P2-1): on 14 days before the next exam in the catalogue (`examOn`, then the
 * omtentor) or for a student who repeterar inför tentan. Null while the catalogue loads; a
 * catalogue that cannot be read (or has no dates) leaves only the student's own answer.
 */
export function useExamPeriod(): ExamModeState | null {
  const query = useCourse();
  const { user } = useAuth();
  if (query.isLoading) return null;
  return examModeState(query.data?.course ?? null, courseToday(), user?.prefs?.position);
}

/** `/practice?course=HT2026&lecture=F03`: the lecture setup page, which waits for "Starta". */
export function lecturePracticeHref(lecture: string, course: string = COURSE_ID): string {
  return `/practice?${new URLSearchParams({ course, lecture })}`;
}

/** A lecture has something to practise: it is published and its pool is not empty. */
function hasPool(lecture: PublicLecture): boolean {
  return lecture.status === "published" && lecture.poolSize > 0;
}

/**
 * A lecture can be practised when it has a pool and its questions are open (P1-12). A teacher
 * (`bypassRelease`) practises every lecture, as the server allows.
 */
export function canPractise(lecture: PublicLecture, bypassRelease = false): boolean {
  return hasPool(lecture) && (bypassRelease || lecture.release?.open !== false);
}

/** The lecture has questions, but they are not open to this student yet (P1-12). */
export function isUnreleased(lecture: PublicLecture, bypassRelease = false): boolean {
  return hasPool(lecture) && !bypassRelease && lecture.release?.open === false;
}

/** "Öppnas tors 5 nov" or, closed by hand, "Stängd tills vidare". */
export function releaseText(
  release: { opensAt: number | null } | undefined,
  t: TFunction,
  lang: Lang,
): string {
  return release?.opensAt != null
    ? t("content.lecture.opens", {
        date: formatDayShort(dayKey(release.opensAt, DEFAULT_TIMEZONE), lang),
      })
    : t("content.lecture.closed");
}

/**
 * The lock shared by every chosen topic, or undefined when at least one of them can be practised
 * (P1-12). The earliest opening wins; all closed by hand gives `opensAt: null`.
 */
export function topicsLock(
  topics: readonly string[],
  locked: Record<string, { opensAt: number | null }> | undefined,
): { opensAt: number | null } | undefined {
  if (!locked || topics.length === 0) return undefined;
  const locks = topics.map((topic) => locked[topic]);
  if (locks.some((lock) => lock === undefined)) return undefined;
  const times = locks
    .map((lock) => lock?.opensAt ?? null)
    .filter((time): time is number => time !== null);
  return { opensAt: times.length > 0 ? Math.min(...times) : null };
}

/**
 * The Y&F sections behind a lecture's topics, in book order and compressed
 * ("38.1, 39.5"). The catalogue has no reading list of its own, so this is what the topics point
 * at, not the lecture plan's reading (which may add or leave out a section).
 */
export function lectureSections(
  topics: readonly string[],
  bookOf: (topic: string) => BookRef | undefined,
): string[] {
  const seen = new Set<string>();
  const sections: string[] = [];
  for (const id of topics) {
    for (const section of bookOf(id)?.sections ?? []) {
      if (seen.has(section)) continue;
      seen.add(section);
      sections.push(section);
    }
  }
  const byNumber = (s: string) => s.split(".").map(Number) as [number, number];
  sections.sort((a, b) => {
    const [ca, sa] = byNumber(a);
    const [cb, sb] = byNumber(b);
    return ca - cb || sa - sb;
  });
  return compressSections(sections);
}
